import React, { useState } from 'react';
import './TimelineProfile.css';

function TimelineProfile({ onToolHover }) {
  const [expandedLog, setExpandedLog] = useState(null);

  // Skills data with usage context
  const skills = {
    languages: [
      { name: 'Python', usage: 'Automation & Exploit Tweaking', levelPercent: 85, icon: '🐍' },
      { name: 'Bash', usage: 'System Administration & One-liners', levelPercent: 75, icon: '⚡' },
      { name: 'JavaScript', usage: 'DOM Analysis & Client-side Attacks', levelPercent: 70, icon: '💛' },
      { name: 'SQL', usage: 'Database Structure & Injection', levelPercent: 65, icon: '🗄️' }
    ],
    tools: [
      { 
        name: 'Metasploit', 
        category: 'attack', 
        color: '#ff006e', 
        command: 'msfconsole -q -x "use exploit/multi/handler; set PAYLOAD windows/meterpreter/reverse_tcp; set LHOST 10.10.14.5; exploit"',
        description: 'Sets up a listener to catch reverse shells from compromised systems. This creates a persistent connection allowing remote command execution on the target machine.'
      },
      { 
        name: 'Hydra', 
        category: 'attack', 
        color: '#ff006e', 
        command: 'hydra -l admin -P /usr/share/wordlists/rockyou.txt ssh://192.168.1.100 -t 4 -V',
        description: 'Performs a brute-force attack against SSH login with username "admin" using the rockyou wordlist. Useful for testing password strength and identifying weak credentials.'
      },
      { 
        name: 'Hashcat', 
        category: 'attack', 
        color: '#ff006e', 
        command: 'hashcat -m 1000 -a 0 hashes.txt /usr/share/wordlists/rockyou.txt --force',
        description: 'Cracks NTLM password hashes using dictionary attack mode. Commonly used in penetration testing to recover passwords from captured hash files.'
      },
      { 
        name: 'Wireshark', 
        category: 'analysis', 
        color: '#4a9eff', 
        command: 'tshark -i eth0 -f "tcp port 80" -w capture.pcap',
        description: 'Captures HTTP traffic on port 80 for network analysis. Helps identify security issues like unencrypted credentials or suspicious traffic patterns.'
      },
      { 
        name: 'Nmap', 
        category: 'analysis', 
        color: '#4a9eff', 
        command: 'nmap -sC -sV -p- --min-rate 1000 -oA scan_results 192.168.1.0/24',
        description: 'Comprehensive network scan with script scanning and version detection on all ports. Discovers live hosts, open ports, and running services across the entire subnet.'
      },
      { 
        name: 'Netcat', 
        category: 'analysis', 
        color: '#4a9eff', 
        command: 'nc -lvnp 4444 # Listening for reverse shell',
        description: 'Opens a listener on port 4444 to catch reverse shell connections. Essential for post-exploitation and maintaining access to compromised systems.'
      },
      { 
        name: 'Burp Suite', 
        category: 'web', 
        color: '#fb923c', 
        command: 'burpsuite --config-file=custom.json --project-file=webapp_test.burp',
        description: 'Launches Burp Suite with custom configuration for web application security testing. Used to intercept, modify, and analyze HTTP/HTTPS traffic.'
      },
      { 
        name: 'ffuf', 
        category: 'web', 
        color: '#fb923c', 
        command: 'ffuf -u http://target.com/FUZZ -w /usr/share/seclists/Discovery/Web-Content/common.txt -mc 200,301,302 -c',
        description: 'Fast web fuzzing to discover hidden directories and files. Helps identify sensitive endpoints that aren\'t linked from the main site.'
      },
      { 
        name: 'SQLMap', 
        category: 'web', 
        color: '#fb923c', 
        command: 'sqlmap -u "http://target.com/page?id=1" --batch --dbs --random-agent',
        description: 'Automated SQL injection testing to enumerate databases. Detects and exploits SQL vulnerabilities to extract database information.'
      }
    ],
    os: ['Kali Linux', 'Parrot OS', 'Ubuntu', 'Windows Server']
  };

  // System Logs (Timeline converted) - The Journey
  const systemLogs = [
    {
      id: 1,
      timestamp: '2022-08',
      level: 'EDUCATION',
      message: 'Started B.Tech in Computer Science & Engineering',
      status: 'ENROLLED',
      details: 'Began my journey in Computer Science at university, building a strong foundation in programming, data structures, and algorithms.',
      color: '#4a9eff'
    },
    {
      id: 2,
      timestamp: '2023-05',
      level: 'FOCUS',
      message: 'Shifted interest from General Coding to Cybersecurity',
      status: 'PIVOT',
      details: 'Discovered my passion for cybersecurity and ethical hacking. Started exploring penetration testing, network security, and CTF challenges.',
      color: '#FFD700'
    },
    {
      id: 3,
      timestamp: '2024-02',
      level: 'SKILLS',
      message: 'Mastered Linux & Solved 150+ Labs on TryHackMe',
      status: 'LEARNING',
      details: 'Achieved proficiency in Linux system administration and completed extensive hands-on practice through TryHackMe platform, solving practical security challenges.',
      color: '#06b6d4'
    },
    {
      id: 4,
      timestamp: '2024-11',
      level: 'CERTIFIED',
      message: 'Achieved eJPT (Junior Penetration Tester) Certification',
      status: 'SUCCESS',
      details: 'Successfully earned the eJPT certification from eLearnSecurity, demonstrating practical penetration testing skills and knowledge.',
      color: '#10b981'
    },
    {
      id: 5,
      timestamp: '2024-12',
      level: 'COMMUNITY',
      message: 'Launched YouTube channel to share security knowledge',
      status: 'ONLINE',
      details: 'Started creating educational content on cybersecurity, sharing my learning journey, tips, and tutorials to help others in the community.',
      color: '#BD93F9'
    },
    {
      id: 6,
      timestamp: 'PRESENT',
      level: 'TARGET',
      message: 'Building tools & preparing for Security+',
      status: 'IN PROGRESS',
      details: 'Actively working on security tools, pursuing CompTIA Security+ certification, and continuously expanding knowledge in both offensive and defensive security.',
      color: '#FF5500'
    }
  ];



  return (
    <div className="dossier-container">
      {/* LEFT COLUMN: ID CARD */}
      <div className="id-card-column">
        <div className="id-card">
          {/* Avatar Placeholder (no photo) */}
          <div className="avatar-placeholder">
            {/* Custom Cyber Avatar */}
            <svg className="avatar-svg" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              {/* Background Circuit Pattern */}
              <defs>
                <pattern id="circuit" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M0 20h10M30 20h10M20 0v10M20 30v10" stroke="rgba(74, 158, 255, 0.3)" strokeWidth="1"/>
                  <circle cx="20" cy="20" r="2" fill="#4a9eff"/>
                </pattern>
                <linearGradient id="glowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{stopColor: '#4a9eff', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#06b6d4', stopOpacity: 1}} />
                </linearGradient>
                <linearGradient id="visorGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{stopColor: '#06b6d4', stopOpacity: 1}} />
                  <stop offset="50%" style={{stopColor: '#0ff', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#06b6d4', stopOpacity: 1}} />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              {/* Outer Hexagon Border */}
              <polygon 
                points="100,10 170,50 170,150 100,190 30,150 30,50" 
                fill="url(#circuit)" 
                opacity="0.2"
                className="hex-bg"
              />
              <polygon 
                points="100,10 170,50 170,150 100,190 30,150 30,50" 
                fill="none" 
                stroke="url(#glowGradient)" 
                strokeWidth="2"
                filter="url(#glow)"
                className="hex-border"
              />
              
              {/* Binary Code Background */}
              <text x="30" y="40" fill="rgba(6, 182, 212, 0.2)" fontSize="8" fontFamily="monospace" className="binary-text">01001000</text>
              <text x="120" y="55" fill="rgba(6, 182, 212, 0.2)" fontSize="8" fontFamily="monospace" className="binary-text">11010110</text>
              <text x="40" y="160" fill="rgba(6, 182, 212, 0.2)" fontSize="8" fontFamily="monospace" className="binary-text">10110011</text>
              <text x="130" y="175" fill="rgba(6, 182, 212, 0.2)" fontSize="8" fontFamily="monospace" className="binary-text">01110100</text>
              
              {/* Hacker Silhouette - Hooded Figure */}
              <g className="hacker-figure">
                {/* Hood */}
                <path 
                  d="M100,50 L70,70 L65,100 L70,120 L100,140 L130,120 L135,100 L130,70 Z" 
                  fill="rgba(10, 0, 30, 0.9)" 
                  stroke="#06b6d4" 
                  strokeWidth="1.5"
                />
                
                {/* Hood Shadow - Inner */}
                <ellipse cx="100" cy="90" rx="20" ry="25" fill="rgba(0, 0, 0, 0.8)"/>
                
                {/* Glowing Visor/Eyes */}
                <rect 
                  x="80" 
                  y="85" 
                  width="40" 
                  height="6" 
                  fill="url(#visorGlow)" 
                  filter="url(#glow)"
                  className="visor"
                  rx="2"
                />
                
                {/* Mysterious Glow under hood */}
                <ellipse cx="100" cy="88" rx="15" ry="8" fill="rgba(6, 182, 212, 0.3)" className="face-glow"/>
                
                {/* Shoulders */}
                <path 
                  d="M70,120 L60,140 L55,155 L70,155 L70,140" 
                  fill="rgba(10, 0, 30, 0.8)" 
                  stroke="#06b6d4" 
                  strokeWidth="1"
                />
                <path 
                  d="M130,120 L140,140 L145,155 L130,155 L130,140" 
                  fill="rgba(10, 0, 30, 0.8)" 
                  stroke="#06b6d4" 
                  strokeWidth="1"
                />
                
                {/* Torso */}
                <rect 
                  x="75" 
                  y="130" 
                  width="50" 
                  height="35" 
                  fill="rgba(10, 0, 30, 0.9)" 
                  stroke="#06b6d4" 
                  strokeWidth="1"
                  rx="2"
                />
                
                {/* Chest Detail - Circuit Lines */}
                <line x1="85" y1="140" x2="95" y2="140" stroke="rgba(74, 158, 255, 0.4)" strokeWidth="0.5"/>
                <line x1="105" y1="140" x2="115" y2="140" stroke="rgba(74, 158, 255, 0.4)" strokeWidth="0.5"/>
                <circle cx="100" cy="145" r="3" fill="none" stroke="rgba(74, 158, 255, 0.4)" strokeWidth="0.5"/>
                <line x1="100" y1="148" x2="100" y2="155" stroke="rgba(74, 158, 255, 0.4)" strokeWidth="0.5"/>
              </g>
              
              {/* Corner Brackets */}
              <path d="M40,40 L40,60 M40,40 L60,40" stroke="#4a9eff" strokeWidth="2" opacity="0.6" className="bracket"/>
              <path d="M160,40 L160,60 M160,40 L140,40" stroke="#4a9eff" strokeWidth="2" opacity="0.6" className="bracket"/>
              <path d="M40,160 L40,140 M40,160 L60,160" stroke="#4a9eff" strokeWidth="2" opacity="0.6" className="bracket"/>
              <path d="M160,160 L160,140 M160,160 L140,160" stroke="#4a9eff" strokeWidth="2" opacity="0.6" className="bracket"/>
              
              {/* Scanning Line Animation */}
              <line x1="50" y1="100" x2="150" y2="100" stroke="#06b6d4" strokeWidth="1" opacity="0.6" className="scan-line">
                <animate attributeName="y1" values="60;140;60" dur="3s" repeatCount="indefinite"/>
                <animate attributeName="y2" values="60;140;60" dur="3s" repeatCount="indefinite"/>
              </line>
              
              {/* Matrix-style falling numbers (optional) */}
              <text x="45" y="70" fill="rgba(0, 255, 65, 0.3)" fontSize="10" fontFamily="monospace" className="matrix-num">5</text>
              <text x="155" y="90" fill="rgba(0, 255, 65, 0.3)" fontSize="10" fontFamily="monospace" className="matrix-num">7</text>
              <text x="50" y="130" fill="rgba(0, 255, 65, 0.3)" fontSize="10" fontFamily="monospace" className="matrix-num">3</text>
            </svg>
            
            <div className="status-indicator">
              <span className="status-dot"></span>
              <span className="status-text">ONLINE</span>
            </div>
          </div>

          {/* Name & Class */}
          <div className="id-info">
            <h2 className="id-name">Diptanshu Dhawan</h2>
            <div className="id-class">Junior Penetration Tester</div>
            <div className="id-level">B.Tech CSE Student</div>
          </div>

          {/* Certification Badge */}
          <div className="certification-section">
            <div className="cert-label">CERTIFIED</div>
            <a 
              href="https://certs.ine.com/f11bccd4-1a89-4329-9a0c-eead32ea9583#acc.g3Onli2Z" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cert-badge"
            >
              <img src="/eJPT logo.png" alt="eJPT" className="cert-badge-icon" />
              <div className="cert-badge-info">
                <div className="cert-name">eJPT</div>
                <div className="cert-issuer">eLearnSecurity</div>
                <div className="cert-date">Nov 2025</div>
              </div>
            </a>
          </div>

          {/* TryHackMe Badge */}
          <div className="tryhackme-section">
            <div className="thm-label">TRYHACKME PROFILE</div>
            <a 
              href="https://tryhackme.com/p/diptanshudhawan" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <img 
                src="https://tryhackme-badges.s3.amazonaws.com/diptanshudhawan.png" 
                alt="TryHackMe Badge" 
                className="thm-badge-img"
              />
            </a>
          </div>

          {/* YouTube Channel */}
          <div className="youtube-section">
            <div className="yt-label">YOUTUBE CHANNEL</div>
            <a 
              href="https://www.youtube.com/@HackWithDD" 
              target="_blank" 
              rel="noopener noreferrer"
              className="yt-card"
            >
              <div className="yt-icon">
                <svg width="40" height="28" viewBox="0 0 40 28" fill="none">
                  <path d="M39.1 4.4C38.7 2.9 37.5 1.7 36 1.3C32.9 0.5 20 0.5 20 0.5C20 0.5 7.1 0.5 4 1.3C2.5 1.7 1.3 2.9 0.9 4.4C0.1 7.5 0.1 14 0.1 14C0.1 14 0.1 20.5 0.9 23.6C1.3 25.1 2.5 26.3 4 26.7C7.1 27.5 20 27.5 20 27.5C20 27.5 32.9 27.5 36 26.7C37.5 26.3 38.7 25.1 39.1 23.6C39.9 20.5 39.9 14 39.9 14C39.9 14 39.9 7.5 39.1 4.4Z" fill="#FF0000"/>
                  <path d="M16 20L26 14L16 8V20Z" fill="white"/>
                </svg>
              </div>
              <div className="yt-info">
                <div className="yt-name">Diptanshu Dhawan</div>
                <div className="yt-meta">Cybersecurity Content</div>
              </div>
            </a>

            {/* Latest Video Release */}
            <div className="yt-latest-release">
              <div className="yt-latest-label">LATEST RELEASE</div>
              <a 
                href="https://youtu.be/tnKJZ1VsdqI" 
                target="_blank" 
                rel="noopener noreferrer"
                className="yt-video-card"
              >
                <div className="yt-thumbnail">
                  <img 
                    src="https://img.youtube.com/vi/tnKJZ1VsdqI/hqdefault.jpg" 
                    alt="Latest Video"
                    className="yt-thumb-img"
                  />
                  <div className="yt-play-overlay">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
                <div className="yt-video-title">
                  Nmap Explained | The ONLY Guide You Need in 2026

                </div>
                <div className="yt-watch-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                  </svg>
                  Watch Now
                </div>
              </a>
            </div>
          </div>

          {/* Primary Stats */}
          <div className="id-stats">
            <div className="stat-item">
              <div className="stat-value">150+</div>
              <div className="stat-label">Labs Solved</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">500+</div>
              <div className="stat-label">Flags Captured</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">∞</div>
              <div className="stat-label">Coffee</div>
            </div>
          </div>

          {/* Download Button */}
          <a 
            href="/resume.pdf" 
            download 
            className="download-dossier-btn"
          >
            <span className="btn-icon">⬇️</span>
            GET MY RESUME
          </a>
        </div>
      </div>

      {/* RIGHT COLUMN: SYSTEM SPECS */}
      <div className="system-specs-column">
        
        {/* Section A: Bio (Terminal Style) */}
        <div className="terminal-bio">
          <div className="terminal-header">
            <span className="terminal-prompt">root@diptanshu:~$</span>
            <span className="terminal-command">cat bio.txt</span>
          </div>
          <div className="terminal-output">
            <div className="bio-line">
              <span className="bio-arrow">&gt;</span> Loading subject data...
            </div>
            <div className="bio-line">
              <span className="bio-arrow">&gt;</span> Subject is a <span className="highlight">B.Tech CSE student</span> specializing in <span className="highlight">Cyber security</span>.
            </div>
            <div className="bio-line">
              <span className="bio-arrow">&gt;</span> Primary directives: <span className="highlight">Penetration Testing</span>, <span className="highlight">Vulnerability Research</span>, and <span className="highlight">Security Automation</span>.
            </div>
            <div className="bio-line">
              <span className="bio-arrow">&gt;</span> Mission: Breaking systems to build them stronger.
            </div>
          </div>
        </div>

        {/* Section B: Tech Stack */}
        <div className="tech-stack-section">
          <h3 className="section-title">
            <span className="title-icon">⚡</span> THE ARSENAL
          </h3>
          
          {/* Languages with Skill Bars */}
          <div className="skill-category">
            <div className="category-label">SCRIPTING & AUTOMATION</div>
            <div className="skill-bars">
              {skills.languages.map((skill, idx) => (
                <div key={idx} className="skill-bar-item">
                  <div className="skill-bar-header">
                    <span className="skill-icon">{skill.icon}</span>
                    <span className="skill-name">{skill.name}</span>
                    <span className="skill-usage">{skill.usage}</span>
                  </div>
                  <div className="skill-bar-track">
                    <div 
                      className="skill-bar-fill" 
                      style={{ width: `${skill.levelPercent}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tools */}
          <div className="skill-category">
            <div className="category-label">TOOLS</div>
            <div style={{
              fontSize: '11px',
              color: '#64748b',
              fontStyle: 'italic',
              marginBottom: '8px',
              padding: '6px 10px',
              background: 'rgba(6, 182, 212, 0.05)',
              borderLeft: '3px solid #06b6d4',
              borderRadius: '4px'
            }}>
              💡 Click on any tool to see its implementation
            </div>
            <div className="tool-tags">
              {skills.tools.map((tool, idx) => (
                <span 
                  key={idx} 
                  className="tool-tag"
                  style={{
                    borderLeft: `3px solid ${tool.color}`,
                    borderColor: tool.color,
                    boxShadow: `0 0 10px ${tool.color}20`,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    // Trigger terminal command preview on click
                    if (onToolHover) {
                      onToolHover(tool.command, tool.name, tool.description);
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 15px ${tool.color}60, 0 0 5px ${tool.color}40`;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 10px ${tool.color}20`;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {tool.name}
                </span>
              ))}
            </div>
          </div>

          {/* Operating Systems */}
          <div className="skill-category">
            <div className="category-label">OPERATING SYSTEMS</div>
            <div className="os-tags">
              {skills.os.map((os, idx) => (
                <span key={idx} className="os-tag">
                  <span className="os-icon">🐧</span>
                  {os}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Section C: System Logs (Timeline) */}
        <div className="system-logs-section">
          <h3 className="section-title">
            <span className="title-icon">📋</span> EXECUTION HISTORY
          </h3>
          <div className="logs-container">
            {systemLogs.map((log, idx) => (
              <div 
                key={log.id} 
                className={`log-entry ${expandedLog === log.id ? 'expanded' : ''}`}
                onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
              >
                <div className="log-main">
                  <span className="log-timestamp">[{log.timestamp}]</span>
                  <span className={`log-level level-${log.level.toLowerCase()}`}>[{log.level}]</span>
                  <span className="log-message">{log.message}</span>
                  <span className={`log-status status-${log.status.toLowerCase()}`}>{log.status}</span>
                </div>
                {expandedLog === log.id && (
                  <div className="log-details" style={{ borderLeftColor: log.color }}>
                    {log.details}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Current Objectives Section */}
        <div className="objectives-section">
          <h3 className="section-title">
            <span className="title-icon">🎯</span> CURRENT OBJECTIVES
          </h3>
          
          <div className="objectives-list">
            {/* Completed Objective */}
            <div className="objective-card completed">
              <div className="objective-icon">✓</div>
              <div className="objective-content">
                <div className="objective-status">COMPLETED</div>
                <div className="objective-title">eJPT Certification</div>
                <div className="objective-detail">Junior Penetration Tester certification from eLearnSecurity</div>
              </div>
            </div>

            {/* In Progress Objective */}
            <div className="objective-card in-progress">
              <div className="objective-icon">⟳</div>
              <div className="objective-content">
                <div className="objective-status">IN PROGRESS</div>
                <div className="objective-title">CompTIA Security+</div>
                <div className="objective-detail">Expanding defensive security (Blue Team) knowledge to complement offensive eJPT skills</div>
              </div>
            </div>

            {/* Target Objective */}
            <div className="objective-card target">
              <div className="objective-icon">!</div>
              <div className="objective-content">
                <div className="objective-status">TARGET</div>
                <div className="objective-title">OSCP Certification</div>
                <div className="objective-detail">Advanced penetration testing certification goal</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default TimelineProfile;
